import React, {Fragment} from 'react';
import {FormInputBorder} from 'module/CustomField/component/FormInputBorder';
import {useSelector} from 'react-redux';
import {CF} from 'module/CustomField/util/CustomField';
import {CustomFieldGroupedMembersComponentProps} from 'module/CustomField/type/CFComponentType';
import {AddCFRowButton} from 'module/CustomField/component/button/AddCFRowButton';
import {ClearCFRowsButton} from 'module/CustomField/component/button/ClearCFRowsButton';
import {CFAttrKey} from 'module/CustomField/constant/CFAttrConstants'
import {CustomFieldMembersComponent} from 'module/CustomField/component/CustomFieldMembersComponent'
import {CFRowButtonsComponent} from 'module/CustomField/component/button/CFRowButtonsComponent'
import {CustomFieldTO} from 'generated/JavaTO'
import {cssCFLabelSticky, cssCFSubLabelSticky} from 'module/CustomField/util/cssCFLabelStickyStyles'
import {selCFGroupById} from 'module/CFGroup/selector/CFGroupSelector'
import {InputType} from 'module/CustomField/util/CFInputType'
import {css} from '@emotion/react'
import {cx} from '@emotion/css'

const cssGroupBase = css`
	height: 100%;
`

const cssGroup = css`
	border: 1px solid #a3a3a3;
	border-radius: 4px;
`
const cssGroupedTable = css`
	height: 100%;
	width: 100%;
	display: grid;
	align-items: end;

	& > .CustomFieldGroupBg:last-child {
		border-radius: 0 0 3px 3px;
	}
`
const cssTableGap = css`
	row-gap: 4px;
`
const cssGroupBg = css`
	height: 100%;
	width: 100%;
	pointer-events: none;
`
let cssGroupDivider = css`
	margin: 2px 0;
	height: 2px;
	width: 100%;
	background: #a3a3a3;
`
const cssHeader = css`
	display: flex;
	background: #e9e9e9;
	align-items: center;
	flex-wrap: wrap;
	height: 32px;
	padding-left: 8px;
	border-radius: 4px;
`
const cssAddNonExistent = css`
	opacity: 0.4;
	text-align: center;
`
const cssFormHeaderLabel = css`
	padding-right: 4px;
`

export const CustomFieldGroupedMembersComponent = React.memo<CustomFieldGroupedMembersComponentProps>((props) => {
	const cfGroupById = useSelector(selCFGroupById)
	const {
		groupId, cfDefCells, colSpan, rowSpan, forceEnableText, changePermitted, persistedModel, actionPending, model,
		setCFValue, deleteCFValue, moveCFValue, validateForm,
	} = props
	const cfDefs = cfDefCells.map(({cfDef}) => cfDef)
	const group = groupId != null ? cfGroupById[groupId] : undefined
	const mergeAmountButtons = group?.mergeAmountButtons || false

	const {cf} = model || {}

	const positions = CF.getPositions(cfDefs, model)
	const positionsLen = positions.length

	let showAddGroupButton = false
	let showCleanGroupButton = false
	const memberMetaByCFDefId = cfDefs.reduce((s, cfDef) => {
		const positionsLen = CF.getPositions([cfDef], model).length
		const x = {
			showAdd: !!changePermitted && positionsLen < cfDef.maxAmount && !CF.isReadOnlyInput(cfDef),
			showClean: !!changePermitted && positionsLen > cfDef.minAmount && !CF.isReadOnlyInput(cfDef),
			hideLabel: CF.getCFAttr(cfDef, CFAttrKey.hideLabel)?.value === 'J' || false,
			labelDisplayedInValue: cfDef.input === InputType.LinkButton,
			inlineLabel: CF.getCFAttr(cfDef, CFAttrKey.inlineLabel)?.value === 'J' || false,
			isCFNamespace: CF.isCFNamespace(cfDef),
			cfNamespaceField: CF.getCFNamespaceField(cfDef),
			isLabelField: CF.isLabelField(cfDef),
		}
		if (x.showAdd) showAddGroupButton = true
		if (x.showClean) showCleanGroupButton = true
		s[cfDef.id] = x
		return s
	}, {} as Record<number, {
		showAdd: boolean
		showClean: boolean
		hideLabel: boolean
		labelDisplayedInValue: boolean
		inlineLabel: boolean
		isCFNamespace: boolean
		cfNamespaceField: string
		isLabelField: boolean
	}>)

	const onClickAdd = (cfDefs: CustomFieldTO[]) => {
		cfDefs = cfDefs.filter(x => CF.isCFNamespace(x))
		const pos = CF.getNextPos(cfDefs, model)
		cfDefs.forEach(cfDef => {
			if (memberMetaByCFDefId[cfDef.id].showAdd) {
				setCFValue({cfDef, value: cfDef.defaultValue, options: {pos}})
			}
		})
		if (validateForm) {
			validateForm({checkRemoteCollision: false});
		}
	}
	const onClickClearAll = () => {
		deleteCFValue?.({cfDefs: cfDefs, clearNullValues: true})
	}
	const onClickDeleteRow = (pos: number) => {
		deleteCFValue?.({cfDefs: cfDefs, pos});
	}
	const onClickMove = !moveCFValue || cfDefs.some(cfDef => CF.getCFAttr(cfDef, CFAttrKey.moveCF)?.value === 'N') ? undefined : ((pos: number, up: boolean) => {
		moveCFValue?.({cfDefs: cfDefs, pos, up})
	})

	const isArray = cfDefs.some(cfDef => cfDef.maxAmount > 1)
	const hasMultipleMembers = positionsLen > 1
	const showHeader = group?.heading || mergeAmountButtons && cfDefs.some(cfDef => cfDef.maxAmount > cfDef.minAmount)
	const labelAddColSpan = !group && hasMultipleMembers ? 1 : 0
	const singleLine = Boolean(group && rowSpan < 2 || cfDefs.length === 1)

	return (
		<div
			css={[cssGroupBase, ...(group ? [cssGroup, !singleLine ? cssTableGap : undefined] : [])]}
			className={cx(
				'CustomFieldFormGroup',
				(singleLine ? 'SingleLine' : 'MultiLine'),
				{
					'Group': !!group,
					'Array': isArray,
				},
			)}>
			{showHeader &&
				<div css={[cssCFLabelSticky, cssHeader]} className={'CustomFieldFormHeader'}>
					{group?.heading && <div className={'CustomFieldFormHeaderLabel'} css={cssFormHeaderLabel}>{group.heading}</div>}
					{mergeAmountButtons && showAddGroupButton && <AddCFRowButton onClick={() => onClickAdd(cfDefs)}/>}
					{mergeAmountButtons && showCleanGroupButton && <ClearCFRowsButton onClick={onClickClearAll}/>}
				</div>
			}
			<div
				css={[
					cssGroupedTable,
					css` grid-template:  ${singleLine ? 'min-content ' : ''}${rowSpan * positionsLen}fr / [group-start] repeat(${colSpan}, 1fr)${hasMultipleMembers ? ' min-content' : ''} [group-end];`,
				]}
				className={'CustomFieldFormGroupedTable'}
			>
				{positions.map((pos, posIndex) => {
					const isFirst = posIndex === 0
					const isLast = posIndex === positionsLen - 1
					const groupRowSpan = rowSpan
					let colPosStart = cfDefCells.reduce((ret, cell) => ret > cell.colPos ? cell.colPos : ret, Infinity)
					const zebraClassName = posIndex % 2 ? 'odd' : 'even'
					return <Fragment key={posIndex}>
						{singleLine && isFirst && cfDefCells.map(({cfDef, colPos, colSpan}) => {
								const meta = memberMetaByCFDefId[cfDef.id]
								const showAddButton = meta.showAdd
								const showCleanButton = meta.showClean
								const arrayButtonVisible = !mergeAmountButtons && (showAddButton || showCleanButton)
								/* labels are only displayed on top for single line groups */
								const label = meta.hideLabel ? '' : meta.inlineLabel || meta.labelDisplayedInValue ? '&nbsp;' : cfDef.label
								return !arrayButtonVisible && !label ? null : <Fragment key={cfDef.id}>
									<div
										className={'CFGroupButtons ' + zebraClassName}
										css={[
											group?.heading || group?.mergeAmountButtons ? cssCFSubLabelSticky : cfDef.maxAmount > 1 ? cssCFLabelSticky : undefined,
											css` grid-area: 1 / ${colPos} / span 1 / span ${colSpan + labelAddColSpan};`,
										]}
									>
										<FormInputBorder
											label={label}
											required={cfDef.required}
											afterLabel={arrayButtonVisible && <div>
												{showAddButton &&
													<AddCFRowButton onClick={() => onClickAdd([cfDef])}/>
												}
												{showCleanButton &&
													<ClearCFRowsButton onClick={() => deleteCFValue?.({cfDefs: [cfDef], clearNullValues: true})}/>
												}
											</div>}
										/>
									</div>
								</Fragment>
							},
						)}
						{!singleLine && !isFirst && <div
							className={'CustomFieldGroupDivider ' + zebraClassName}
							css={[
								cssGroupDivider,
								css` grid-area: ${posIndex * (rowSpan + 1)} / group-start / span 1 / group-end; `,
							]}
						/>}
						<div
							className={'CustomFieldGroupBg ' + zebraClassName}
							css={[
								cssGroupBg,
								css`grid-area: ${posIndex * rowSpan + 1 + (+singleLine || posIndex)} / ${colPosStart} / span ${rowSpan} / span ${colSpan + +hasMultipleMembers};`,
							]}
						/>
						{cfDefCells.map(({cfDef, rowPos, colPos, rowSpan, colSpan}) => {
							const meta = memberMetaByCFDefId[cfDef.id]
							const isNonValueField = CF.isNonValueField(cfDef)
							const posInRange = posIndex < cfDef.maxAmount
							let isNonExistent = false
							if (meta.isCFNamespace) {
								const dst = meta.cfNamespaceField
								const v = cf?.find(x => x.pos === pos && x.dstField === dst)
								if (!v) isNonExistent = true
							} else if (
								posIndex > 0 && !isNonValueField ||
								// show LinkButton only when in min/max-range
								isNonValueField && !posInRange
							) {
								isNonExistent = true
							}
							return <div
								key={cfDef.id + ':' + pos}
								className={'CustomFieldGroupRowCell ' + zebraClassName}
								css={css` grid-area: ${groupRowSpan * posIndex + rowPos + (+singleLine || posIndex)} / ${colPos} / span ${rowSpan} / span ${colSpan}; `}
							>
								{!meta.hideLabel && meta.isLabelField && posInRange &&
									<FormInputBorder
										label={singleLine ? undefined : cfDef.label}
										required={cfDef.required}
									/>
									||
									isNonExistent && meta.showAdd &&
									<div css={cssAddNonExistent}>
										<FormInputBorder
											label={singleLine ? undefined : cfDef.label}
											required={cfDef.required}
										>
											<AddCFRowButton onClick={() => onClickAdd([cfDef])}/>
										</FormInputBorder>
									</div>
								}
								{!isNonExistent &&
									<CustomFieldMembersComponent
										{...props}
										cfDef={cfDef}
										group={group}
										labelDone={!meta.inlineLabel && singleLine || meta.labelDisplayedInValue}
										labelHidden={meta.hideLabel}
										inlineLabel={meta.inlineLabel}
										pos={hasMultipleMembers ? pos : undefined}
										hideArrayButtons={mergeAmountButtons || singleLine}
										disabled={
											actionPending ||
											(typeof forceEnableText === 'boolean' && cfDef.dstField === 'text' ? !forceEnableText : !changePermitted) ||
											(persistedModel ? !cfDef.updatable : !cfDef.insertable) ||
											CF.getCFAttr(cfDef, CFAttrKey.disabled)?.value === 'J'
										}
									/>
								}
							</div>
						})}
						{changePermitted && hasMultipleMembers &&
							<div
								className={'CustomFieldGroupRowControl ' + zebraClassName}
								css={css`
									grid-area: ${posIndex * rowSpan + 1 + (+singleLine || posIndex)} / ${colSpan + 1} / span ${rowSpan} / span 1;
									align-self: center;
								`}
							>
								<CFRowButtonsComponent
									onClickDelete={(() => typeof pos === 'number' && onClickDeleteRow(pos)) || undefined}
									onClickUp={onClickMove && !isFirst && (() => typeof pos === 'number' && onClickMove(pos, true)) || undefined}
									onClickDown={onClickMove && !isLast && (() => typeof pos === 'number' && onClickMove(pos, false)) || undefined}
									disabled={!!actionPending}
									wrap={!singleLine}
								/>
							</div>
						}
					</Fragment>
				})}
			</div>
		</div>
	)
})
